import { CSSProperties, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { css, cx } from '@emotion/css';
import type { Placement } from '@floating-ui/core';

import Tooltip from './Tooltip';

export type TruncatedTextWithTooltipContentProps = {
    ref: (node: HTMLElement | null) => void;
    className: string;
    onMouseEnter: () => void;
};

type Props = {
    text?: string;
    renderContent?: (props: TruncatedTextWithTooltipContentProps) => ReactNode;
    tooltipText?: ReactNode;
    shouldAlwaysShowTooltip?: boolean;
    shouldSuppressTooltip?: boolean;
    lines?: number;
    className?: string;
    placement?: Placement;
    wrapperStyles?: CSSProperties;
    tooltipStyle?: CSSProperties;
    maxContentHeight?: number;
};

const DEFAULT_WRAPPER_STYLES: CSSProperties = { display: 'block', minWidth: 0, width: '100%' };

const getClampClass = (lines: number) =>
    lines === 1
        ? css({
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
          })
        : css({
              display: '-webkit-box',
              WebkitLineClamp: lines,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minWidth: 0,
              overflowWrap: 'anywhere',
          });

const TruncatedTextWithTooltip: FC<Props> = ({
    text,
    renderContent,
    tooltipText,
    shouldAlwaysShowTooltip = false,
    shouldSuppressTooltip = false,
    lines = 1,
    className,
    placement,
    wrapperStyles,
    tooltipStyle,
    maxContentHeight,
}) => {
    const [node, setNode] = useState<HTMLElement | null>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const checkTruncation = useCallback(() => {
        if (node) {
            setIsTruncated(
                node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth
            );
        }
    }, [node]);

    useEffect(() => {
        if (!node) {
            return;
        }

        checkTruncation();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', checkTruncation);
            return () => window.removeEventListener('resize', checkTruncation);
        }

        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(node);
        return () => resizeObserver.disconnect();
    }, [node, text, lines, checkTruncation]);

    const contentProps = useMemo<TruncatedTextWithTooltipContentProps>(
        () => ({
            ref: setNode,
            className: cx(getClampClass(lines), className),
            onMouseEnter: checkTruncation,
        }),
        [lines, className, checkTruncation]
    );

    const showTooltip = !shouldSuppressTooltip && (shouldAlwaysShowTooltip || isTruncated);
    const title = tooltipText ?? text;

    return (
        <Tooltip
            title={showTooltip ? title : undefined}
            placement={placement}
            wrapperStyles={{ ...DEFAULT_WRAPPER_STYLES, ...wrapperStyles }}
            style={tooltipStyle}
            maxContentHeight={maxContentHeight}
        >
            {renderContent ? renderContent(contentProps) : <div {...contentProps}>{text}</div>}
        </Tooltip>
    );
};

export default TruncatedTextWithTooltip;
